// Trailing Stop-Loss Engine
// Core logic for monitoring price movements and adjusting stop-loss orders

import type {
  Position,
  TrailingStopLossConfig,
  TrailingStopLossUpdate,
  TrailingStopLossEvent,
  CreateOrderRequest
} from '../types/mexc';
import { TrailingStopLossPersistenceService } from './trailingStopLossPersistence';
import { PositionPersistenceService } from './positionPersistence';
import { getMexcApiClient } from './mexcApi';
import { tslActivityService } from './tslActivityService';
import { tslLogger, tickerLogger } from '../utils/logger';

export class TrailingStopLossEngine {
  private static instance: TrailingStopLossEngine;
  private isRunning = false;
  private monitoringInterval: NodeJS.Timeout | null = null;
  private cleanupInterval: NodeJS.Timeout | null = null;
  private priceHealthInterval: NodeJS.Timeout | null = null;
  private eventListeners: Array<(event: TrailingStopLossEvent) => void> = [];
  private lastPriceUpdates: Record<string, { price: number; timestamp: number }> = {};
  private lastProcessTime = 0;
  private readonly PRICE_REFRESH_INTERVAL = parseInt(import.meta.env.VITE_PRICE_REFRESH_INTERVAL) || 60000; // 1 minute (synced with price polling)
  private readonly MAX_PRICE_CACHE_SIZE = 500; // Limit price cache to prevent memory growth
  private readonly PRICE_CACHE_MAX_AGE = 30 * 60 * 1000; // 30 minutes max age for price data
  private autoTSLEnabled = true; // Enable auto-TSL by default
  private processedPositionIds = new Set<number>(); // Track positions we've seen to detect new ones
  private isProcessing = false; // Prevent concurrent processing

  // Singleton pattern
  static getInstance(): TrailingStopLossEngine {
    if (!TrailingStopLossEngine.instance) {
      TrailingStopLossEngine.instance = new TrailingStopLossEngine();
    }
    return TrailingStopLossEngine.instance;
  }

  /**
   * Start the trailing stop-loss monitoring engine
   */
  start(): void {
    if (this.isRunning) {
      // Silently skip - engine already running
      return;
    }

    this.isRunning = true;
    console.log(`🚀 Starting Trailing Stop-Loss Engine (${this.PRICE_REFRESH_INTERVAL / 1000}s intervals)`);

    // Process TSL every 30 seconds, but only act on price updates that respect the 1-minute interval
    this.monitoringInterval = setInterval(() => {
      this.processTrailingStopLosses();
    }, 30000); // Check every 30 seconds instead of 5 seconds

    // Cleanup stale price data every 1 minute to prevent memory growth
    this.cleanupInterval = setInterval(() => {
      this.cleanupStalePriceData();
    }, 1 * 60 * 1000);

    // Check price health every 2 minutes to warn about stale prices
    this.priceHealthInterval = setInterval(() => {
      this.checkPriceHealth();
    }, 2 * 60 * 1000);

    this.emitEvent({
      type: 'enabled',
      positionId: 0,
      symbol: 'SYSTEM',
      timestamp: Date.now(),
      message: `TSL Engine started with ${this.PRICE_REFRESH_INTERVAL / 1000}s price refresh interval`,
    });
  }

  /**
   * Stop the trailing stop-loss monitoring engine
   */
  stop(): void {
    if (!this.isRunning) return;

    this.isRunning = false;
    console.log('Stopping Trailing Stop-Loss Engine');

    if (this.monitoringInterval) {
      clearInterval(this.monitoringInterval);
      this.monitoringInterval = null;
    }

    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
      this.cleanupInterval = null;
    }

    if (this.priceHealthInterval) {
      clearInterval(this.priceHealthInterval);
      this.priceHealthInterval = null;
    }

    // Clear cached data to free memory
    this.lastPriceUpdates = {};
    this.processedPositionIds.clear();

    this.emitEvent({
      type: 'disabled',
      positionId: 0,
      symbol: 'SYSTEM',
      timestamp: Date.now(),
      message: 'TSL Engine stopped',
    });
  }

  /**
   * Cleanup stale price data to prevent memory growth
   */
  private cleanupStalePriceData(): void {
    const now = Date.now();
    const symbolsToRemove: string[] = [];

    // Find stale entries
    for (const [symbol, data] of Object.entries(this.lastPriceUpdates)) {
      if (now - data.timestamp > this.PRICE_CACHE_MAX_AGE) {
        symbolsToRemove.push(symbol);
      }
    }

    // Remove stale entries
    for (const symbol of symbolsToRemove) {
      delete this.lastPriceUpdates[symbol];
    }

    // If still too many entries, remove oldest ones
    const entries = Object.entries(this.lastPriceUpdates);
    if (entries.length > this.MAX_PRICE_CACHE_SIZE) {
      const sorted = entries.sort((a, b) => a[1].timestamp - b[1].timestamp);
      const toRemove = sorted.slice(0, entries.length - this.MAX_PRICE_CACHE_SIZE);
      for (const [symbol] of toRemove) {
        delete this.lastPriceUpdates[symbol];
      }
    }

    if (symbolsToRemove.length > 0) {
      console.log(`🧹 TSL Engine: Cleaned up ${symbolsToRemove.length} stale price entries`);
    }
  }

  /**
   * Check price health and warn about stale prices for TSL-enabled positions
   */
  private checkPriceHealth(): void {
    const now = Date.now();
    const tslState = TrailingStopLossPersistenceService.getTSLState();
    const activeConfigs = Object.values(tslState.configs).filter(
      config => config.enabled && config.isActive
    );

    if (activeConfigs.length === 0) return;

    const staleThreshold = 1 * 60 * 1000; // 1 minute - warn if price is older than this
    const criticalThreshold = 2 * 60 * 1000; // 2 minutes - critical if price is older than this
    const staleSymbols: string[] = [];
    const criticalSymbols: string[] = [];
    const missingSymbols: string[] = [];

    for (const config of activeConfigs) {
      const priceData = this.lastPriceUpdates[config.symbol];

      if (!priceData) {
        missingSymbols.push(config.symbol);
        continue;
      }

      const age = now - priceData.timestamp;
      if (age > criticalThreshold) {
        criticalSymbols.push(`${config.symbol} (${Math.round(age / 1000)}s old)`);
      } else if (age > staleThreshold) {
        staleSymbols.push(`${config.symbol} (${Math.round(age / 1000)}s old)`);
      }
    }

    // Log warnings for price health issues
    if (missingSymbols.length > 0) {
      console.warn(`🚨 TSL Price Health: Missing price data for ${missingSymbols.length} TSL-enabled symbols:`, missingSymbols);
      tslLogger.warn(`Missing price data for TSL symbols: ${missingSymbols.join(', ')}`);
    }

    if (criticalSymbols.length > 0) {
      console.warn(`🔴 TSL Price Health: CRITICAL - Stale prices (>2min) for:`, criticalSymbols);
      tslLogger.tslCritical(`Critical stale prices: ${criticalSymbols.join(', ')}`);
    }

    if (staleSymbols.length > 0) {
      console.warn(`🟡 TSL Price Health: Warning - Stale prices (>1min) for:`, staleSymbols);
      tslLogger.warn(`Stale prices: ${staleSymbols.join(', ')}`);
    }

    // Log healthy status if all prices are fresh
    if (missingSymbols.length === 0 && criticalSymbols.length === 0 && staleSymbols.length === 0) {
      console.log(`✅ TSL Price Health: All ${activeConfigs.length} TSL-enabled symbols have fresh price data`);
    }
  }

  /**
   * Update price for a symbol (called from WebSocket or price updates)
   */
  updatePrice(symbol: string, price: number): void {
    this.lastPriceUpdates[symbol] = {
      price,
      timestamp: Date.now(),
    };
  }

  /**
   * Enable or disable auto-TSL feature
   */
  setAutoTSLEnabled(enabled: boolean): void {
    this.autoTSLEnabled = enabled;
    console.log(`🔄 Auto-TSL feature ${enabled ? 'enabled' : 'disabled'}`);
  }

  /**
   * Check if auto-TSL is enabled
   */
  isAutoTSLEnabled(): boolean {
    return this.autoTSLEnabled;
  }

  /**
   * Process all active trailing stop-losses
   */
  private async processTrailingStopLosses(): Promise<void> {
    return this.processTrailingStopLossesInternal(false);
  }

  /**
   * Force immediate TSL processing (bypasses 1-minute interval)
   * Used for manual testing and debugging
   */
  async forceProcessTrailingStopLosses(): Promise<void> {
    console.log('🚀 FORCE MODE: Bypassing 1-minute interval for immediate TSL processing');
    return this.processTrailingStopLossesInternal(true);
  }

  /**
   * Internal method to process trailing stop-losses with optional force mode
   */
  private async processTrailingStopLossesInternal(forceMode: boolean = false): Promise<void> {
    // Prevent concurrent processing to avoid race conditions
    if (this.isProcessing) {
      console.log('⏳ TSL processing already in progress, skipping...');
      return;
    }

    try {
      this.isProcessing = true;
      const now = Date.now();

      // Check if enough time has passed since last processing (respecting 1-minute interval)
      // Skip this check if in force mode
      if (!forceMode && this.lastProcessTime > 0 && (now - this.lastProcessTime) < this.PRICE_REFRESH_INTERVAL) {
        const timeRemaining = Math.round((this.PRICE_REFRESH_INTERVAL - (now - this.lastProcessTime)) / 1000);
        console.log(`⏰ TSL processing skipped - ${timeRemaining}s remaining until next interval`);
        return;
      }

      // Start grouped logging for TSL processing cycle
      tslLogger.tslProcessingStart(forceMode ? 'forced' : 'scheduled');

      this.lastProcessTime = now;

      const tslState = TrailingStopLossPersistenceService.getTSLState();

      if (!tslState.globalEnabled) {
        tslLogger.info('TSL processing skipped - Global TSL disabled');
        tslLogger.tslProcessingEnd(false);
        return;
      }

      // Get current positions with fallback to cached data FIRST
      // This is needed for auto-TSL detection before checking active configs
      const positions = await this.getPositionsWithFallback();

      if (positions.length === 0) {
        console.log('⚠️ No positions available (API failed and no cached data), skipping TSL processing');
        tslLogger.tslProcessingEnd(false);
        return;
      }

      tslLogger.info(`Using ${positions.length} positions for TSL processing`);

      // Auto-apply TSL to new positions if enabled
      // This MUST happen BEFORE checking active configs, otherwise new positions
      // without TSL configs would be missed due to the early return
      if (this.autoTSLEnabled) {
        await this.autoApplyTSLToNewPositions(positions);
      }

      // Now get active configs (may include newly auto-applied ones)
      // Re-fetch state since autoApplyTSLToNewPositions may have added new configs
      const updatedTslState = TrailingStopLossPersistenceService.getTSLState();
      const activeConfigs = Object.values(updatedTslState.configs).filter(
        config => config.enabled && config.isActive
      );

      if (activeConfigs.length === 0) {
        tslLogger.info('TSL processing completed - No active TSL configurations to process');
        tslLogger.tslProcessingEnd(true);
        return;
      }

      // Update ticker logger with TSL-enabled symbols for priority logging
      const tslSymbols = activeConfigs.map(config => config.symbol);
      tickerLogger.updatePrioritySymbols(tslSymbols);

      console.log(`📊 Processing ${activeConfigs.length} active TSL configurations`);

      for (const config of activeConfigs) {
        const position = positions.find(p => p.positionId === config.positionId);
        if (!position) {
          // Position no longer exists, deactivate TSL
          tslLogger.tslCritical(`Position ${config.positionId} not found, deactivating TSL`);
          await this.deactivateTSL(config.positionId, 'Position closed');
          continue;
        }

        // Group position-specific processing
        tslLogger.tslPositionStart(position.symbol, position.positionId);
        await this.processPositionTSL(position, config);
        tslLogger.tslPositionEnd();
      }

      // End processing cycle logging
      tslLogger.tslProcessingEnd(true);
    } catch (error) {
      tslLogger.error('Error processing trailing stop-losses', error);
      tslLogger.tslProcessingEnd(false);
    } finally {
      this.isProcessing = false;
    }
  }

  /**
   * Get positions with fallback to cached data when API fails
   */
  private async getPositionsWithFallback(): Promise<Position[]> {
    try {
      const mexcApi = getMexcApiClient();
      const apiPositions = await mexcApi.getPositions();

      if (apiPositions && apiPositions.length > 0) {
        console.log(`✅ TSL Engine: Using ${apiPositions.length} positions from API`);
        return apiPositions;
      }

      // API returned empty or failed, try cached positions
      console.log('⚠️ TSL Engine: API returned no positions, checking cache...');
      const cachedPositions = PositionPersistenceService.loadPositions();

      if (cachedPositions.length > 0) {
        console.log(`🔄 TSL Engine: Using ${cachedPositions.length} cached positions`);
        return cachedPositions;
      }

      console.log('❌ TSL Engine: No positions available from API or cache');
      return [];
    } catch (error) {
      console.log('❌ TSL Engine: API error, falling back to cached positions');
      console.error('API Error:', error);

      const cachedPositions = PositionPersistenceService.loadPositions();
      if (cachedPositions.length > 0) {
        console.log(`🔄 TSL Engine: Using ${cachedPositions.length} cached positions after API failure`);
        return cachedPositions;
      }

      return [];
    }
  }

  /**
   * Auto-apply TSL to new positions that don't have TSL configurations
   */
  private async autoApplyTSLToNewPositions(positions: Position[]): Promise<void> {
    try {
      const tslState = TrailingStopLossPersistenceService.getTSLState();
      const defaultSettings = tslState.defaultSettings;

      for (const position of positions) {
        // Skip if position already has TSL configuration
        if (tslState.configs[position.positionId]) {
          this.processedPositionIds.add(position.positionId);
          continue;
        }

        // Skip if we've already processed this position in this session
        if (this.processedPositionIds.has(position.positionId)) {
          continue;
        }

        // Mark as processed
        this.processedPositionIds.add(position.positionId);

        // Create auto-TSL configuration using default settings
        const autoConfig: TrailingStopLossConfig = {
          positionId: position.positionId,
          symbol: position.symbol,
          enabled: true,
          isActive: true,
          trailingType: defaultSettings.trailingType,
          trailingDistance: defaultSettings.trailingDistance,
          minProfitThreshold: defaultSettings.minProfitThreshold,
          createdAt: Date.now(),
          updatedAt: Date.now(),
        };

        // Save the auto-applied configuration
        TrailingStopLossPersistenceService.saveTSLConfig(autoConfig);

        // Log the auto-application
        console.log(`✅ Auto-TSL Applied to new position ${position.symbol} (ID: ${position.positionId}):`, {
          trailingType: autoConfig.trailingType,
          trailingDistance: autoConfig.trailingDistance,
          minProfitThreshold: autoConfig.minProfitThreshold,
          positionType: position.positionType === 1 ? 'LONG' : 'SHORT',
          positionSize: position.holdVol,
        });

        // Log activity
        tslActivityService.logActivity({
          symbol: position.symbol,
          positionId: position.positionId,
          eventType: 'enable',
          currentMarketPrice: this.getCurrentPrice(position.symbol) || 0,
          reason: `Auto-TSL applied to new position using default settings (${autoConfig.trailingType}: ${autoConfig.trailingDistance}${autoConfig.trailingType === 'percentage' ? '%' : ''})`,
          success: true,
          trailingDistance: autoConfig.trailingDistance,
          trailingType: autoConfig.trailingType,
        });

        // Emit event
        this.emitEvent({
          type: 'enabled',
          positionId: position.positionId,
          symbol: position.symbol,
          timestamp: Date.now(),
          message: `Auto-TSL enabled for new position: ${position.symbol}`,
          data: autoConfig,
        });
      }
    } catch (error) {
      console.error('Error auto-applying TSL to new positions:', error);
      tslLogger.error('Error auto-applying TSL to new positions', error);
    }
  }

  /**
   * Process trailing stop-loss for a specific position
   */
  private async processPositionTSL(
    position: Position,
    config: TrailingStopLossConfig
  ): Promise<void> {
    try {
      console.log(`🔄 Processing TSL for ${position.symbol} (Position ID: ${position.positionId})`);

      let currentPrice = this.getCurrentPrice(position.symbol);

      // If no cached price available, try to fetch from API as fallback
      if (!currentPrice) {
        console.log(`⚠️ No cached price for ${position.symbol}, attempting API fallback...`);
        try {
          const mexcApi = getMexcApiClient();
          const tickerResult = await mexcApi.getTicker(position.symbol);
          // Handle both single Ticker and Ticker[] response
          const ticker = Array.isArray(tickerResult)
            ? tickerResult.find(t => t.symbol === position.symbol)
            : tickerResult;
          if (ticker && ticker.lastPrice && ticker.lastPrice > 0) {
            currentPrice = ticker.lastPrice;
            this.updatePrice(position.symbol, currentPrice); // Cache the fetched price
            console.log(`✅ API fallback successful for ${position.symbol}: ${currentPrice}`);
          }
        } catch (apiError) {
          console.warn(`❌ API fallback failed for ${position.symbol}:`, apiError);
        }
      }

      // If still no price after fallback, skip processing but log detailed warning
      if (!currentPrice) {
        console.warn(`❌ No current price available for ${position.symbol} (cache miss + API fallback failed)`);
        console.log(`📊 Available cached prices:`, Object.keys(this.lastPriceUpdates));

        // Log activity for visibility
        tslActivityService.logActivity({
          symbol: position.symbol,
          positionId: position.positionId,
          eventType: 'error',
          currentMarketPrice: 0,
          reason: `TSL skipped: No price data available (WebSocket disconnected and API fallback failed)`,
          success: false,
          trailingDistance: config.trailingDistance,
          trailingType: config.trailingType,
        });

        return;
      }

      const isLong = position.positionType === 1;
      const entryPrice = position.openAvgPrice;

      console.log(`📈 TSL Analysis for ${position.symbol}:`, {
        currentPrice,
        entryPrice,
        positionType: isLong ? 'LONG' : 'SHORT',
        positionSize: position.holdVol,
        unrealizedPnl: 0 // Position doesn't have unrealizedPnl, calculate if needed
      });

      // Check if minimum profit threshold is met
      const currentProfit = isLong
        ? currentPrice - entryPrice
        : entryPrice - currentPrice;

      const profitPercentage = (currentProfit / entryPrice) * 100;

      console.log(`💰 Profit Analysis:`, {
        currentProfit: currentProfit.toFixed(6),
        profitPercentage: profitPercentage.toFixed(4) + '%',
        minProfitThreshold: config.minProfitThreshold ? config.minProfitThreshold.toFixed(4) + '%' : 'None',
        thresholdMet: !config.minProfitThreshold || profitPercentage >= config.minProfitThreshold
      });

      if (config.minProfitThreshold && profitPercentage < config.minProfitThreshold) {
        console.log(`⏸️ TSL paused: Profit ${profitPercentage.toFixed(4)}% below threshold ${config.minProfitThreshold.toFixed(4)}%`);
        return;
      }

      // Update high/low water marks
      const updatedConfig = this.updateWaterMarks(config, currentPrice, isLong);

      console.log(`🌊 Water Marks:`, {
        highWaterMark: updatedConfig.highWaterMark,
        lowWaterMark: updatedConfig.lowWaterMark,
        waterMarkUpdated: updatedConfig !== config
      });

      // Calculate new trailing stop price
      const newStopPrice = this.calculateTrailingStopPrice(
        updatedConfig,
        currentPrice,
        isLong
      );

      if (!newStopPrice) {
        console.log(`⏸️ No trailing stop price calculated`);
        return;
      }

      console.log(`🎯 Trailing Stop Calculation:`, {
        calculatedPrice: newStopPrice,
        symbol: config.symbol,
        trailingType: config.trailingType,
        trailingDistance: config.trailingDistance,
        calculatedFrom: isLong ? 'highWaterMark' : 'lowWaterMark',
        note: 'Price precision formatting will be applied by MEXC API client based on contract details'
      });

      // Get current stop-loss price
      const currentStopPrice = await this.getCurrentStopLossPrice(position);

      console.log(`🛑 Stop-Loss Comparison:`, {
        currentStopPrice: currentStopPrice?.toFixed(6) || 'None',
        newStopPrice: newStopPrice.toFixed(6),
        improvement: currentStopPrice ? (isLong ? newStopPrice > currentStopPrice : newStopPrice < currentStopPrice) : true
      });

      // Enhanced validation before updating stop-loss
      const validationResult = this.validateStopLossUpdate(
        position,
        currentStopPrice,
        newStopPrice,
        currentPrice,
        isLong
      );

      if (!validationResult.isValid) {
        console.log(`❌ Stop-loss update validation failed: ${validationResult.reason}`);

        // Log validation failure
        tslActivityService.logActivity({
          symbol: position.symbol,
          positionId: position.positionId,
          eventType: 'error',
          currentMarketPrice: currentPrice,
          reason: `Validation failed: ${validationResult.reason}`,
          success: false,
          trailingDistance: config.trailingDistance,
          trailingType: config.trailingType,
          highWaterMark: config.highWaterMark,
          lowWaterMark: config.lowWaterMark
        });

        return;
      }

      // Check if we need to update the stop-loss
      const shouldUpdate = this.shouldUpdateStopLoss(
        currentStopPrice,
        newStopPrice,
        isLong
      );

      if (shouldUpdate) {
        console.log(`✅ Updating stop-loss order from ${currentStopPrice?.toFixed(6) || 'None'} to ${newStopPrice.toFixed(6)}`);
        await this.updateStopLossOrder(position, newStopPrice, updatedConfig);
      } else {
        console.log(`⏸️ Stop-loss update not needed (no improvement)`);

        // Log why update was skipped
        tslActivityService.logActivity({
          symbol: position.symbol,
          positionId: position.positionId,
          eventType: 'update',
          previousStopPrice: currentStopPrice || undefined,
          newStopPrice: newStopPrice,
          currentMarketPrice: currentPrice,
          reason: `Stop-loss update skipped - no improvement (${isLong ? 'new price not higher' : 'new price not lower'})`,
          success: false,
          trailingDistance: config.trailingDistance,
          trailingType: config.trailingType,
          highWaterMark: config.highWaterMark,
          lowWaterMark: config.lowWaterMark
        });
      }

      // Save updated config
      if (updatedConfig !== config) {
        TrailingStopLossPersistenceService.saveTSLConfig(updatedConfig);
        console.log(`💾 TSL configuration updated and saved`);
      }

    } catch (error) {
      console.error(`Error processing TSL for position ${position.positionId}:`, error);

      this.emitEvent({
        type: 'error',
        positionId: position.positionId,
        symbol: position.symbol,
        timestamp: Date.now(),
        message: `TSL processing error: ${error instanceof Error ? error.message : String(error)}`,
        data: { error },
      });
    }
  }

  /**
   * Update high/low water marks for tracking price extremes
   */
  private updateWaterMarks(
    config: TrailingStopLossConfig,
    currentPrice: number,
    isLong: boolean
  ): TrailingStopLossConfig {
    const updatedConfig = { ...config };
    let waterMarkUpdated = false;

    if (isLong) {
      // For long positions, track highest price
      const previousHigh = updatedConfig.highWaterMark;
      if (!updatedConfig.highWaterMark || currentPrice > updatedConfig.highWaterMark) {
        updatedConfig.highWaterMark = currentPrice;
        updatedConfig.updatedAt = Date.now();
        waterMarkUpdated = true;

        console.log(`📈 High water mark updated for ${config.symbol}:`, {
          previousHigh: previousHigh?.toFixed(6) || 'None',
          newHigh: currentPrice.toFixed(6),
          improvement: previousHigh ? ((currentPrice - previousHigh) / previousHigh * 100).toFixed(4) + '%' : 'Initial'
        });
      }
    } else {
      // For short positions, track lowest price
      const previousLow = updatedConfig.lowWaterMark;
      if (!updatedConfig.lowWaterMark || currentPrice < updatedConfig.lowWaterMark) {
        updatedConfig.lowWaterMark = currentPrice;
        updatedConfig.updatedAt = Date.now();
        waterMarkUpdated = true;

        console.log(`📉 Low water mark updated for ${config.symbol}:`, {
          previousLow: previousLow?.toFixed(6) || 'None',
          newLow: currentPrice.toFixed(6),
          improvement: previousLow ? ((previousLow - currentPrice) / previousLow * 100).toFixed(4) + '%' : 'Initial'
        });
      }
    }

    if (!waterMarkUpdated) {
      console.log(`🌊 Water mark unchanged for ${config.symbol}:`, {
        currentPrice: currentPrice.toFixed(6),
        waterMark: isLong
          ? (updatedConfig.highWaterMark?.toFixed(6) || 'None')
          : (updatedConfig.lowWaterMark?.toFixed(6) || 'None'),
        type: isLong ? 'High' : 'Low',
        reason: isLong
          ? `Current price not higher than high water mark`
          : `Current price not lower than low water mark`
      });
    }

    return updatedConfig;
  }

  /**
   * Calculate the new trailing stop price
   */
  private calculateTrailingStopPrice(
    config: TrailingStopLossConfig,
    _currentPrice: number, // unused but kept for API consistency
    isLong: boolean
  ): number | null {
    const waterMark = isLong ? config.highWaterMark : config.lowWaterMark;
    if (!waterMark) return null;

    let newStopPrice: number;

    if (config.trailingType === 'percentage') {
      const trailingAmount = waterMark * (config.trailingDistance / 100);
      newStopPrice = isLong
        ? waterMark - trailingAmount  // Long: stop below high water mark
        : waterMark + trailingAmount; // Short: stop above low water mark
    } else {
      // Fixed amount trailing
      newStopPrice = isLong
        ? waterMark - config.trailingDistance
        : waterMark + config.trailingDistance;
    }

    // Return the calculated price - precision formatting will be handled by MEXC API client
    return newStopPrice;
  }



  /**
   * Validate stop-loss update to ensure it's safe and profitable
   */
  private validateStopLossUpdate(
    position: Position,
    currentStopPrice: number | null,
    newStopPrice: number,
    currentPrice: number,
    isLong: boolean
  ): { isValid: boolean; reason: string } {
    const entryPrice = position.openAvgPrice;

    // Validation 1: Ensure new stop loss is in the profitable direction
    if (isLong) {
      // For long positions, stop loss should be below current price and above entry (if profitable)
      if (newStopPrice >= currentPrice) {
        return {
          isValid: false,
          reason: `Long position: New stop loss ${newStopPrice.toFixed(6)} is at or above current price ${currentPrice.toFixed(6)}`
        };
      }

      // Only allow stop loss below entry if position is currently losing money
      const currentProfit = currentPrice - entryPrice;
      if (currentProfit > 0 && newStopPrice < entryPrice) {
        return {
          isValid: false,
          reason: `Long position in profit: Stop loss ${newStopPrice.toFixed(6)} should not be below entry price ${entryPrice.toFixed(6)}`
        };
      }
    } else {
      // For short positions, stop loss should be above current price and below entry (if profitable)
      if (newStopPrice <= currentPrice) {
        return {
          isValid: false,
          reason: `Short position: New stop loss ${newStopPrice.toFixed(6)} is at or below current price ${currentPrice.toFixed(6)}`
        };
      }

      // Only allow stop loss above entry if position is currently losing money
      const currentProfit = entryPrice - currentPrice;
      if (currentProfit > 0 && newStopPrice > entryPrice) {
        return {
          isValid: false,
          reason: `Short position in profit: Stop loss ${newStopPrice.toFixed(6)} should not be above entry price ${entryPrice.toFixed(6)}`
        };
      }
    }

    // Validation 2: Ensure stop loss only moves in the profitable direction
    if (currentStopPrice) {
      if (isLong && newStopPrice <= currentStopPrice) {
        return {
          isValid: false,
          reason: `Long position: New stop loss ${newStopPrice.toFixed(6)} is not higher than current ${currentStopPrice.toFixed(6)}`
        };
      }

      if (!isLong && newStopPrice >= currentStopPrice) {
        return {
          isValid: false,
          reason: `Short position: New stop loss ${newStopPrice.toFixed(6)} is not lower than current ${currentStopPrice.toFixed(6)}`
        };
      }
    }

    // Validation 3: Ensure reasonable distance from current price (prevent too tight stops)
    const distanceFromCurrent = Math.abs(newStopPrice - currentPrice);
    const minDistance = currentPrice * 0.001; // 0.1% minimum distance

    if (distanceFromCurrent < minDistance) {
      return {
        isValid: false,
        reason: `Stop loss ${newStopPrice.toFixed(6)} is too close to current price ${currentPrice.toFixed(6)} (distance: ${distanceFromCurrent.toFixed(6)}, minimum: ${minDistance.toFixed(6)})`
      };
    }

    return { isValid: true, reason: 'Validation passed' };
  }

  /**
   * Check if stop-loss should be updated
   */
  private shouldUpdateStopLoss(
    currentStopPrice: number | null,
    newStopPrice: number,
    isLong: boolean
  ): boolean {
    if (!currentStopPrice) {
      return true; // No current stop-loss, set new one
    }

    if (isLong) {
      // For long positions, only move stop-loss up (never down)
      return newStopPrice > currentStopPrice;
    } else {
      // For short positions, only move stop-loss down (never up)
      return newStopPrice < currentStopPrice;
    }
  }

  /**
   * Get current price for a symbol
   */
  private getCurrentPrice(symbol: string): number | null {
    const priceUpdate = this.lastPriceUpdates[symbol];
    if (!priceUpdate) {
      console.log(`❌ No price update found for ${symbol}`);
      return null;
    }

    // For 1-minute intervals, allow prices up to 2 minutes old
    // This accommodates the VITE_PRICE_REFRESH_INTERVAL=60000ms (1 minute)
    const now = Date.now();
    const maxAge = 2 * 60 * 1000; // 2 minutes in milliseconds
    const age = now - priceUpdate.timestamp;

    if (age > maxAge) {
      console.log(`⏰ Price for ${symbol} is too old: ${Math.round(age / 1000)}s (max: ${maxAge / 1000}s)`);
      return null;
    }

    console.log(`✅ Using price for ${symbol}: ${priceUpdate.price} (age: ${Math.round(age / 1000)}s)`);
    return priceUpdate.price;
  }

  /**
   * Get current stop-loss price for a position (REVERTED to simple approach)
   */
  private async getCurrentStopLossPrice(position: Position): Promise<number | null> {
    try {
      const mexcApi = getMexcApiClient();

      // Use the documented working approach
      const stopOrders = await mexcApi.getStopOrders(position.symbol);

      const associatedStopOrder = stopOrders.find(order =>
        String(order.positionId) === String(position.positionId) &&
        order.state === 1
      );

      return associatedStopOrder?.stopLossPrice || null;
    } catch (error) {
      console.error('Error getting current stop-loss price:', error);
      return null;
    }
  }

  /**
   * Get current take-profit price for a position
   */
  private async getCurrentTakeProfitPrice(position: Position): Promise<number | null> {
    try {
      const mexcApi = getMexcApiClient();

      // Use the documented working approach
      const stopOrders = await mexcApi.getStopOrders(position.symbol);

      const associatedStopOrder = stopOrders.find(order =>
        String(order.positionId) === String(position.positionId) &&
        order.state === 1
      );

      return associatedStopOrder?.takeProfitPrice || null;
    } catch (error) {
      console.error('Error getting current take-profit price:', error);
      return null;
    }
  }

  /**
   * Calculate adjusted take profit price when trailing stop loss
   * Maintains risk-reward ratio while allowing profits to run
   */
  private async calculateAdjustedTakeProfit(
    position: Position,
    newStopPrice: number,
    config: TrailingStopLossConfig
  ): Promise<number | null> {
    try {
      const isLong = position.positionType === 1;
      const entryPrice = position.openAvgPrice;
      const currentPrice = this.getCurrentPrice(position.symbol);
      const currentTakeProfitPrice = await this.getCurrentTakeProfitPrice(position);

      if (!currentPrice) {
        console.log(`⚠️ No current price available for ${position.symbol}, cannot calculate adjusted TP`);
        return currentTakeProfitPrice; // Keep existing TP if we can't calculate
      }

      console.log(`🎯 Take Profit Calculation for ${position.symbol}:`, {
        entryPrice,
        currentPrice,
        newStopPrice,
        currentTakeProfitPrice: currentTakeProfitPrice?.toFixed(6) || 'None',
        isLong,
        positionType: isLong ? 'LONG' : 'SHORT'
      });

      // If no existing take profit, don't create one automatically
      if (!currentTakeProfitPrice) {
        console.log(`📝 No existing take profit found, not setting new TP automatically`);
        return null;
      }

      // Calculate current risk and reward distances
      const currentRisk = Math.abs(newStopPrice - entryPrice);
      const currentReward = Math.abs(currentTakeProfitPrice - entryPrice);
      const currentRiskRewardRatio = currentReward / currentRisk;

      console.log(`📊 Current Risk-Reward Analysis:`, {
        currentRisk: currentRisk.toFixed(6),
        currentReward: currentReward.toFixed(6),
        riskRewardRatio: currentRiskRewardRatio.toFixed(2)
      });

      // Strategy 1: Maintain minimum risk-reward ratio (default 2:1)
      const minRiskRewardRatio = config.minRiskRewardRatio || 2.0;
      const minRequiredReward = currentRisk * minRiskRewardRatio;

      let adjustedTakeProfit: number;

      if (isLong) {
        // For long positions: TP should be above entry price
        adjustedTakeProfit = entryPrice + minRequiredReward;

        // Ensure TP doesn't move backwards (should stay above current TP or move higher)
        if (currentTakeProfitPrice && adjustedTakeProfit < currentTakeProfitPrice) {
          adjustedTakeProfit = currentTakeProfitPrice;
          console.log(`📈 Keeping existing TP as it's already better than calculated minimum`);
        }
      } else {
        // For short positions: TP should be below entry price
        adjustedTakeProfit = entryPrice - minRequiredReward;

        // Ensure TP doesn't move backwards (should stay below current TP or move lower)
        if (currentTakeProfitPrice && adjustedTakeProfit > currentTakeProfitPrice) {
          adjustedTakeProfit = currentTakeProfitPrice;
          console.log(`📉 Keeping existing TP as it's already better than calculated minimum`);
        }
      }

      // Return the calculated price - precision formatting will be handled by MEXC API client
      const formattedTakeProfit = adjustedTakeProfit;

      console.log(`🎯 Adjusted Take Profit Calculation:`, {
        minRiskRewardRatio,
        minRequiredReward: minRequiredReward.toFixed(6),
        calculatedTakeProfit: adjustedTakeProfit.toFixed(6),
        finalTakeProfit: formattedTakeProfit.toFixed(6),
        newRiskRewardRatio: (Math.abs(formattedTakeProfit - entryPrice) / currentRisk).toFixed(2),
        note: 'Price precision formatting will be applied by MEXC API client'
      });

      return formattedTakeProfit;

    } catch (error) {
      console.error('Error calculating adjusted take profit:', error);
      // Return current TP to avoid disrupting the stop loss update
      return await this.getCurrentTakeProfitPrice(position);
    }
  }

  /**
   * Update stop-loss order via MEXC API with fallback to create new order
   */
  private async updateStopLossOrder(
    position: Position,
    newStopPrice: number,
    config: TrailingStopLossConfig
  ): Promise<void> {
    try {
      const mexcApi = getMexcApiClient();
      const currentStopPrice = await this.getCurrentStopLossPrice(position);

      // Calculate adjusted take profit price
      const adjustedTakeProfitPrice = await this.calculateAdjustedTakeProfit(
        position,
        newStopPrice,
        config
      );

      console.log(`🎯 TSL Update Strategy:`, {
        currentStopPrice: currentStopPrice?.toFixed(6) || 'None',
        newStopPrice: newStopPrice.toFixed(6),
        adjustedTakeProfitPrice: adjustedTakeProfitPrice?.toFixed(6) || 'None',
        strategy: adjustedTakeProfitPrice ? 'Update both SL and TP' : 'Update SL only'
      });

      // Try to update existing stop order first
      try {
        await mexcApi.updatePositionTPSL(
          position.positionId,
          newStopPrice,
          adjustedTakeProfitPrice ?? undefined // Convert null to undefined
        );

        console.log(`✅ Successfully updated stop-loss order to ${newStopPrice}${adjustedTakeProfitPrice ? ` and take-profit to ${adjustedTakeProfitPrice}` : ''}`);
      } catch (updateError) {
        // If update fails because no existing stop order found, create a new one
        const updateErrorMessage = updateError instanceof Error ? updateError.message : String(updateError);
        if (updateErrorMessage?.includes('No existing stop order found')) {
          console.log(`🔄 No existing stop order found, creating new stop order for position ${position.positionId}`);
          await this.createStopLossOrder(position, newStopPrice);
          console.log(`✅ Successfully created new stop-loss order at ${newStopPrice}`);
        } else {
          // Re-throw other errors
          throw updateError;
        }
      }

      // Update config with new trailing stop price
      const updatedConfig: TrailingStopLossConfig = {
        ...config,
        currentTrailingStopPrice: newStopPrice,
        lastUpdateTime: Date.now(),
        updatedAt: Date.now(),
      };

      TrailingStopLossPersistenceService.saveTSLConfig(updatedConfig);

      // Emit update event
      const updateEvent: TrailingStopLossUpdate = {
        positionId: position.positionId,
        newStopPrice,
        previousStopPrice: currentStopPrice || undefined,
        currentPrice: this.getCurrentPrice(position.symbol) || 0,
        profitAmount: 0, // Calculate if needed
        profitPercentage: 0, // Calculate if needed
        timestamp: Date.now(),
      };

      this.emitEvent({
        type: 'updated',
        positionId: position.positionId,
        symbol: position.symbol,
        timestamp: Date.now(),
        message: `TSL updated: ${newStopPrice}`,
        data: updateEvent,
      });

      console.log(`TSL updated for ${position.symbol}: ${newStopPrice}`);

      // Log successful activity with take profit information
      const currentMarketPrice = this.getCurrentPrice(position.symbol) || 0;
      const profitAmount = position.positionType === 1
        ? (currentMarketPrice - position.openAvgPrice) * Math.abs(position.holdVol)
        : (position.openAvgPrice - currentMarketPrice) * Math.abs(position.holdVol);
      const profitPercentage = ((currentMarketPrice - position.openAvgPrice) / position.openAvgPrice) * 100;

      tslActivityService.logActivity({
        symbol: position.symbol,
        positionId: position.positionId,
        eventType: 'update',
        previousStopPrice: currentStopPrice || undefined,
        newStopPrice: newStopPrice,
        previousTakeProfitPrice: (await this.getCurrentTakeProfitPrice(position)) ?? undefined,
        newTakeProfitPrice: adjustedTakeProfitPrice ?? undefined,
        currentMarketPrice: currentMarketPrice,
        reason: adjustedTakeProfitPrice
          ? `Trailing stop adjusted from ${currentStopPrice?.toFixed(6) || 'None'} to ${newStopPrice.toFixed(6)}, TP adjusted to ${adjustedTakeProfitPrice.toFixed(6)}`
          : `Trailing stop adjusted from ${currentStopPrice?.toFixed(6) || 'None'} to ${newStopPrice.toFixed(6)}`,
        success: true,
        trailingDistance: config.trailingDistance,
        trailingType: config.trailingType,
        highWaterMark: config.highWaterMark,
        lowWaterMark: config.lowWaterMark,
        profitAmount: profitAmount,
        profitPercentage: profitPercentage,
        riskRewardRatio: config.minRiskRewardRatio,
        apiResponseCode: 0
      });

    } catch (error) {
      console.error('Error updating stop-loss order:', error);

      // Log failed activity
      tslActivityService.logActivity({
        symbol: position.symbol,
        positionId: position.positionId,
        eventType: 'error',
        currentMarketPrice: this.getCurrentPrice(position.symbol) || 0,
        reason: `Failed to update trailing stop-loss`,
        success: false,
        errorMessage: error instanceof Error ? error.message : String(error),
        trailingDistance: config.trailingDistance,
        trailingType: config.trailingType,
        highWaterMark: config.highWaterMark,
        lowWaterMark: config.lowWaterMark
      });

      throw error;
    }
  }

  /**
   * Create a new stop-loss order for a position
   */
  private async createStopLossOrder(
    position: Position,
    stopPrice: number
  ): Promise<void> {
    try {
      const mexcApi = getMexcApiClient();

      // Determine order side based on position side
      const isLong = position.positionType === 1;
      const orderSide = isLong ? 2 : 1; // 2 = SELL (for long positions), 1 = BUY (for short positions)

      console.log(`🔄 Creating stop-loss order:`, {
        symbol: position.symbol,
        positionId: position.positionId,
        positionType: position.positionType,
        isLong,
        orderSide,
        stopPrice,
        quantity: Math.abs(position.holdVol)
      });

      // Create stop order using MEXC API
      const orderRequest: CreateOrderRequest = {
        symbol: position.symbol,
        side: orderSide,
        vol: Math.abs(position.holdVol),
        openType: position.openType,
        orderType: 3, // Stop order type
        stopLossPrice: stopPrice,
      };

      const result = await mexcApi.createOrder(orderRequest);
      console.log(`✅ Stop-loss order created successfully:`, result);

    } catch (error) {
      console.error('❌ Failed to create stop-loss order:', error);
      const errorMessage = error instanceof Error ? error.message : String(error);
      throw new Error(`Failed to create stop-loss order: ${errorMessage}`);
    }
  }

  /**
   * Deactivate TSL for a position
   */
  async deactivateTSL(positionId: number, reason: string): Promise<void> {
    const config = TrailingStopLossPersistenceService.getTSLConfig(positionId);
    if (config) {
      const updatedConfig: TrailingStopLossConfig = {
        ...config,
        isActive: false,
        updatedAt: Date.now(),
      };

      TrailingStopLossPersistenceService.saveTSLConfig(updatedConfig);

      // Log TSL deactivation
      tslActivityService.logActivity({
        symbol: config.symbol,
        positionId: config.positionId,
        eventType: 'disable',
        currentMarketPrice: this.getCurrentPrice(config.symbol) || 0,
        reason: reason,
        success: true,
        trailingDistance: config.trailingDistance,
        trailingType: config.trailingType,
        highWaterMark: config.highWaterMark,
        lowWaterMark: config.lowWaterMark
      });

      this.emitEvent({
        type: 'disabled',
        positionId,
        symbol: config.symbol,
        timestamp: Date.now(),
        message: `TSL deactivated: ${reason}`,
      });
    }
  }

  /**
   * Add event listener
   */
  addEventListener(listener: (event: TrailingStopLossEvent) => void): void {
    this.eventListeners.push(listener);
  }

  /**
   * Remove event listener
   */
  removeEventListener(listener: (event: TrailingStopLossEvent) => void): void {
    const index = this.eventListeners.indexOf(listener);
    if (index > -1) {
      this.eventListeners.splice(index, 1);
    }
  }

  /**
   * Emit event to all listeners
   */
  private emitEvent(event: TrailingStopLossEvent): void {
    this.eventListeners.forEach(listener => {
      try {
        listener(event);
      } catch (error) {
        console.error('Error in TSL event listener:', error);
      }
    });
  }

  /**
   * Get engine status
   */
  getStatus(): {
    isRunning: boolean;
    activeConfigs: number;
    lastProcessTime: number | null;
    nextProcessTime: number | null;
    priceRefreshInterval: number;
  } {
    const configs = TrailingStopLossPersistenceService.getAllTSLConfigs();
    const activeConfigs = Object.values(configs).filter(c => c.enabled && c.isActive).length;

    const nextProcessTime = this.lastProcessTime > 0
      ? this.lastProcessTime + this.PRICE_REFRESH_INTERVAL
      : null;

    return {
      isRunning: this.isRunning,
      activeConfigs,
      lastProcessTime: this.lastProcessTime || null,
      nextProcessTime,
      priceRefreshInterval: this.PRICE_REFRESH_INTERVAL,
    };
  }
}
